/// <amd-module name="Wiki/RenameDialog/RenameWikiDialog" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";

import { WikiUpdateParameters, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { getWikiClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import * as Resources from "Wiki/RenameDialog/Resources";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { validateProjectName } from "Wiki/ViewCommon/Utils/ProjectNameValidator";
import { publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface RenameWikiDialogProps {
    wiki: WikiV2;
    onDismiss: () => void;
}

export interface RenameWikiState {
    isRenameInProgress: boolean;
    newName: string;
    errorMessage?: string;
}

export class RenameWikiDialog extends VssComponent<RenameWikiDialogProps, RenameWikiState> {
    constructor(props: RenameWikiDialogProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isRenameInProgress: false,
            newName: this.props.wiki.name
        };
    }

    public render(): JSX.Element {
        const { wiki, onDismiss } = this.props;
        const { isRenameInProgress, newName, errorMessage } = this.state;
        const nameInputClass = "item-name-input";
        // TODO: announce the disclaimer
        return (
            <Dialog
                onDismiss={onDismiss}
                titleProps={{ text: Utils_String.format(Resources.RenameWikiTitleText, wiki.name), className: "rename-wiki-dialog-title" }}
                footerButtonProps={[
                    { text: Resources.RenameWikiCancelButtonText, onClick: onDismiss },
                    {
                        text: Resources.RenameWikiButtonText,
                        danger: true,
                        disabled: isRenameInProgress || !newName || newName == wiki.name || !!errorMessage,
                        onClick: this._onRenameClick
                    }
                ]}
                defaultActiveElement={"." + nameInputClass + " .bolt-textfield-input"}
            >
                <span>
                    {Resources.RenameWikiDisclaimerText}
                    <p />
                </span>

                <FormItem message={errorMessage} error={!!errorMessage}>
                    <TextField
                        value={newName}
                        onChange={this._onTextChange}
                        autoFocus={true}
                        className={nameInputClass}
                        ariaLabel={Resources.RenameWikiInputLabel}
                    />
                </FormItem>
            </Dialog>
        );
    }

    private _onTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        this.setState({
            newName: newValue,
            errorMessage: validateProjectName(newValue).invalidNameReason
        });
    };

    private _onRenameClick = (): void => {
        this.setState({
            isRenameInProgress: true,
            errorMessage: undefined
        });

        this.trackPromise(this._updateWikiName(this.state.newName)).promise.then(
            (updatedWiki: WikiV2) => {
                this.props.onDismiss();
                onClickFPS(this.context.pageContext, this._getUrlWithNewWikiName(updatedWiki), true);
            },
            (error: Error) => {
                publish(this.context.pageContext, TelemetryConstants.WikiRenameFailed, this.props.wiki.type);
                this.setState({
                    errorMessage: error.message,
                    isRenameInProgress: false
                });
            }
        );
    };

    private _updateWikiName = (newName: string): Promise<WikiV2> => {
        const newParams = {
            name: newName
        } as WikiUpdateParameters;
        return getWikiClient(this.context.pageContext).updateWiki(newParams, this.props.wiki.id, this.props.wiki.projectId);
    };

    private _getUrlWithNewWikiName = (updatedWiki: WikiV2): string => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        return historyService.generateUrl(
            {
                wikiIdentifier: updatedWiki.name
            },
            StateMergeOptions.currentState
        );
    };
}

VssComponent.register("Wiki.RenameWikiDialog", RenameWikiDialog);
